import React from 'react';
import {
  ErrorBoundaryProps,
  ErrorBoundaryState,
  ErrorFallbackProps,
  ErrorInfo,
  RecoveryStrategy,
} from './types';
import { FormGroup } from '../../../form/form-groups';

/**
 * Default fallback component for form errors
 */
const DefaultFallback = <T extends Record<string, any>>({
  errorInfo,
  resetErrorBoundary,
  recover,
}: ErrorFallbackProps<T>) => {
  return (
    <div className="reform-error-boundary">
      <h2>Something went wrong in the form</h2>
      <details>
        <summary>Error details</summary>
        <p>{errorInfo.error.message}</p>
        <pre>{errorInfo.error.stack}</pre>
      </details>
      <div className="reform-error-actions">
        <button
          onClick={() => recover(RecoveryStrategy.RESET_FORM)}
          className="reform-error-reset-btn"
        >
          Reset Form
        </button>
        <button onClick={resetErrorBoundary} className="reform-error-retry-btn">
          Try Again
        </button>
      </div>
    </div>
  );
};

/**
 * Error boundary component for Reform forms
 *
 * Catches errors in form components and displays a fallback UI
 *
 * @template T - The type of form data
 *
 * @example
 * // Basic usage
 * <FormErrorBoundary>
 *   <MyFormComponent />
 * </FormErrorBoundary>
 *
 * @example
 * // With custom fallback component
 * <FormErrorBoundary
 *   FallbackComponent={MyErrorFallback}
 *   onError={(error) => logError(error)}
 *   formGroups={reform.getGroups()}
 * >
 *   <MyFormComponent />
 * </FormErrorBoundary>
 */
export class FormErrorBoundary<
  T extends Record<string, any>
> extends React.Component<ErrorBoundaryProps<T>, ErrorBoundaryState<T>> {
  // Add static displayName property
  static displayName = 'FormErrorBoundary';

  constructor(props: ErrorBoundaryProps<T>) {
    super(props);
    this.state = {
      hasError: false,
      errorInfo: null,
    };
  }

  static getDerivedStateFromError(
    error: Error
  ): Partial<ErrorBoundaryState<any>> {
    // Update state so the next render will show the fallback UI
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    // Create error info object
    const fullErrorInfo: ErrorInfo<T> = {
      error,
      errorInfo,
      formGroups: this.props.formGroups,
      context: {},
    };

    // Update state with error info
    this.setState({
      errorInfo: fullErrorInfo,
    });

    // Call onError callback if provided
    if (this.props.onError) {
      this.props.onError(error, errorInfo);
    }
  }

  resetErrorBoundary = () => {
    // Reset the error state
    this.setState({
      hasError: false,
      errorInfo: null,
    });

    // Call onReset callback if provided
    if (this.props.onReset) {
      this.props.onReset();
    }
  };

  recover = (
    strategy: RecoveryStrategy | ((errorInfo: ErrorInfo<T>) => void)
  ) => {
    if (typeof strategy === 'function') {
      // Custom recovery function
      strategy(this.state.errorInfo!);
      this.resetErrorBoundary();
      return;
    }

    // Apply the specified recovery strategy
    switch (strategy) {
      case RecoveryStrategy.RESET_FORM:
        // Reset form logic would be implemented by the consumer
        break;
      case RecoveryStrategy.RETRY_OPERATION:
        // Just reset the error boundary to retry
        break;
      case RecoveryStrategy.IGNORE:
        // Just reset the error boundary
        break;
      case RecoveryStrategy.CUSTOM:
        // Call custom recovery handler if provided
        if (this.props.onCustomRecovery && this.state.errorInfo) {
          this.props.onCustomRecovery(this.state.errorInfo);
        }
        break;
    }

    this.resetErrorBoundary();
  };

  render() {
    const { hasError, errorInfo } = this.state;
    const {
      fallback,
      fallbackRender,
      FallbackComponent = DefaultFallback,
      children,
    } = this.props;

    if (hasError && errorInfo) {
      const fallbackProps: ErrorFallbackProps<T> = {
        errorInfo,
        resetErrorBoundary: this.resetErrorBoundary,
        recover: this.recover,
      };

      // Render the appropriate fallback UI
      if (React.isValidElement(fallback)) {
        return fallback;
      }

      if (typeof fallbackRender === 'function') {
        return fallbackRender(fallbackProps);
      }

      return <FallbackComponent {...fallbackProps} />;
    }

    return children;
  }
}

/**
 * Hook to create a form error boundary with the current form groups
 *
 * @template T - The type of form data
 * @param formGroups - Current form groups
 * @param options - Additional options for the error boundary
 * @returns A FormErrorBoundary component with the current form groups
 *
 * @example
 * // In a form component
 * const reform = useReform(config);
 * const ErrorBoundary = useFormErrorBoundary(reform.getGroups());
 *
 * return (
 *   <ErrorBoundary>
 *     <MyFormFields />
 *   </ErrorBoundary>
 * );
 */
export const useFormErrorBoundary = <T extends Record<string, any>>(
  formGroups: FormGroup<T>[],
  options?: Omit<ErrorBoundaryProps<T>, 'children' | 'formGroups'>
) => {
  return React.useMemo(() => {
    // Create the component with children
    const ErrorBoundaryWrapper = ({
      children,
    }: {
      children: React.ReactNode;
    }) => (
      <FormErrorBoundary<T> formGroups={formGroups} {...options}>
        {children}
      </FormErrorBoundary>
    );

    // Add display name to the component
    ErrorBoundaryWrapper.displayName = 'FormErrorBoundaryWrapper';

    return ErrorBoundaryWrapper;
  }, [formGroups, options]);
};
